import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { nsFormFieldStyles } from '../../../ns-styles/index.js';
import { renderFormField } from '../../../helpers/form-field.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import definition from '@job-schemas/approval.json';
import { jobSchema } from '../job-schema.js';
import { commentNeeded, configOf, summaryRows } from '../generic-logic.js';
import '../../../ns-elements/ns-job-layout.js';
import '../../../ns-elements/ns-detail-list.js';
import '@awesome.me/webawesome/dist/components/button/button.js';

// Approve or reject what the node's config shows, which the engine resolved from earlier steps' answers when it made the job
@customElement(NSApproval.is)
export default class NSApproval extends NSJobComponent {
  static get is() {
    return 'ns-approval';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      icon: 'tick',
      outcomes: { approve: { type: 'hidden' }, reject: { type: 'hidden' } },
    });
  }

  @state() private __comment = '';

  @state() private __error = '';

  static styles = [
    nsFormFieldStyles,
    ...super.styles,
    css`
      textarea {
        width: 100%;
        min-height: 5em;
      }

      .instructions {
        white-space: pre-wrap;
      }
    `,
  ];

  private async __decide(outcome: 'approve' | 'reject') {
    const comment = this.__comment.trim();
    if (!comment && commentNeeded(configOf(this.job), outcome)) {
      this.__error = 'Say why you’re rejecting it.';
      return;
    }
    try {
      this.loading(outcome === 'approve' ? 'Approving' : 'Rejecting');
      await this.outcome(outcome, { ANSWERS: comment ? { comment } : {} });
      this.loaded();
    } catch (err) {
      this.loaded();
      this.failJob(err, outcome === 'approve' ? 'Unable to approve' : 'Unable to reject');
    }
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__decide('reject')}>
        Reject
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__decide('approve')}>Approve</wa-button>
    `;
  }

  render() {
    const config = configOf(this.job);
    const rows = summaryRows(config);
    const { instructions } = config;
    return html`
      <ns-job-layout>
        ${rows.length > 0
          ? html`
              <div class="job-panel">
                <h3>What you’re approving</h3>
                <ns-detail-list .rows=${rows}></ns-detail-list>
              </div>
            `
          : nothing}
        <div class="job-panel">
          ${typeof instructions === 'string' && instructions
            ? html`
                <p class="instructions">${instructions}</p>
              `
            : nothing}
          ${renderFormField({
            id: 'approval-comment',
            label: config.commentOnReject === true ? 'Comment (needed to reject)' : 'Comment (optional)',
            control: html`
              <textarea
                id="approval-comment"
                maxlength="10000"
                .value=${live(this.__comment)}
                @input=${(e: Event) => {
                  this.__comment = (e.target as HTMLTextAreaElement).value;
                  this.__error = '';
                }}
              ></textarea>
            `,
            error: this.__error || undefined,
          })}
        </div>
      </ns-job-layout>
    `;
  }
}
